import axios from 'axios';
import i18n from '../i18n';

const authHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};
const base = '/api/v1/nutrition';

export interface SoilSessionOperator { operatorId: string; name: string }
export interface SoilSessionGroup {
  groupId: string; label: string; orchardNames: string[];
  assignedOperators: SoilSessionOperator[]; status: string;
  plannedKg?: number; treeCount?: number; areaHa?: number; cultivars?: string[];
  // S4 capture fields, present once a group has been started/completed:
  startedAt?: string; completedAt?: string; ecReading?: number; signatureName?: string;
  /** #482: provenance. Already on the wire — getSessionWithFacts returns the
   *  session document whole, with no field projection. Only this type omitted it. */
  captureChannel?: 'field' | 'backcapture';
}
export interface SoilSession {
  _id: string; soilProgramId: string; season: string; month: string; productName: string;
  status: string; orchards: string[]; groups: SoilSessionGroup[];
}
export interface SoilSessionFact { orchardName: string; kg: number; treeCount: number; areaHa: number; cultivar: string }
export interface Operator { operatorId: string; name: string }

export interface SoilInstructionRow {
  label: string; orchards: string[]; cultivars: string[];
  treeCount: number; areaHa: number; plannedKg: number;
}
export interface SoilInstruction {
  session: { season: string; month: string; productName: string; status: string };
  farmName: string;
  period: string;
  rows: SoilInstructionRow[];
  grandTotal: { treeCount: number; areaHa: number; plannedKg: number };
  generated: { exists: boolean; lang?: string; generatedAt?: string; generatedBy?: string } | null;
}

export const soilSessionService = {
  async create(programId: string, month: string, productName?: string): Promise<{ created: SoilSession[]; skipped: string[] }> {
    const body: { month: string; productName?: string } = { month };
    if (productName) body.productName = productName;
    return (await axios.post(`${base}/soil-programs/${programId}/sessions`, body, { headers: authHeaders() })).data;
  },
  async listForProgram(programId: string): Promise<SoilSession[]> {
    return (await axios.get(`${base}/soil-programs/${programId}/sessions`, { headers: authHeaders() })).data;
  },
  async get(id: string): Promise<{ session: SoilSession; facts: SoilSessionFact[] }> {
    return (await axios.get(`${base}/soil-application-sessions/${id}`, { headers: authHeaders() })).data;
  },
  async saveGroups(id: string, groups: Array<Partial<SoilSessionGroup>>): Promise<SoilSession> {
    return (await axios.put(`${base}/soil-application-sessions/${id}/groups`, { groups }, { headers: authHeaders() })).data;
  },
  /**
   * Confirm a grouping. Passing `groups` performs save-then-plan server-side in
   * ONE call, so a rejected plan can never leave persisted groups that this
   * screen's local state does not know about.
   */
  async plan(id: string, groups?: Array<Partial<SoilSessionGroup>>): Promise<SoilSession> {
    const body = groups ? { groups } : {};
    return (await axios.post(`${base}/soil-application-sessions/${id}/plan`, body, { headers: authHeaders() })).data;
  },
  /**
   * `confirm` must be true for a session already in_progress/complete/signed_off
   * — the backend otherwise 409s with CONFIRM_REQUIRED (soilSessionService.js:146,
   * routes/nutrition.js:568) because confirming also reverses any applied
   * quantities off the stock ledger (reverseGroupLedger). Defaults to false so
   * existing/other callers keep their current (pre-confirmation) behaviour;
   * SoilSessionDetailPage passes true from its confirmation modal.
   */
  async cancel(id: string, opts: { confirm?: boolean } = {}): Promise<SoilSession> {
    return (await axios.post(`${base}/soil-application-sessions/${id}/cancel`, { confirm: opts.confirm === true }, { headers: authHeaders() })).data;
  },
  async listOperators(): Promise<Operator[]> {
    const res = await axios.get<Array<{ _id: string; name: string }>>('/api/v1/spray/operators', { headers: authHeaders() });
    return res.data.map((o) => ({ operatorId: o._id, name: o.name }));
  },
  async getInstruction(id: string): Promise<SoilInstruction> {
    return (await axios.get(`${base}/soil-application-sessions/${id}/instruction`, { headers: authHeaders() })).data;
  },
  async downloadInstructionPdf(id: string, opts: { force?: boolean } = {}): Promise<void> {
    const lang = i18n.language || 'en';
    const query = `lang=${encodeURIComponent(lang)}${opts.force ? '&force=true' : ''}`;
    const res = await axios.get(`${base}/soil-application-sessions/${id}/instruction/pdf?${query}`, {
      headers: authHeaders(), responseType: 'blob',
    });
    const url = URL.createObjectURL(res.data);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  },
  async deleteInstructionPdf(id: string): Promise<void> {
    await axios.delete(`${base}/soil-application-sessions/${id}/instruction/pdf`, { headers: authHeaders() });
  },
};
